import { ITEM_LAYOUT } from 'tradera/constants/ui';

type PixelDensity = '1x' | '1.5x' | '2x' | '3x';
// Resolutions are square (equal width & height) that match img-proxy nginx config
// `small` is a 250px JPG fallback for Safari 14-15.6 on macOS 10.x - the rest are WEBP
type ItemImageResolution = 250 | 500 | 750 | 1000 | 'small';

const DEFAULT_RESOLUTION: ItemImageResolution = 500;

export type SrcSetMap = {
    [key in PixelDensity]?: ItemImageResolution;
};

type PixelDensityResolution = {
    [key in ItemImageResolution]?: SrcSetMap;
};

const pixelDensityResolution: PixelDensityResolution = {
    250: {
        '1x': 250,
        '2x': 250,
        '3x': 500
    },
    500: {
        '1x': 250,
        '1.5x': 250,
        '2x': 500
    },
    750: {
        '1x': 500,
        '1.5x': 750
    }
};

const pixelDensityResolutionDesktop: PixelDensityResolution = {
    250: {
        '1x': 250,
        '2x': 500,
        '3x': 750
    },
    500: {
        '1x': 500,
        '1.5x': 750,
        '2x': 1000
    },
    750: {
        '1x': 750,
        '1.5x': 1000
    }
};

type LayoutImageResolution = {
    [key in ITEM_LAYOUT]: ItemImageResolution;
};

const layoutImageResolution: LayoutImageResolution = {
    [ITEM_LAYOUT.LIST]: 250,
    [ITEM_LAYOUT.GRID]: 250,
    [ITEM_LAYOUT.GRID_MEDIUM]: 500,
    [ITEM_LAYOUT.GRID_LARGE]: 750
};

const fullWidthLayoutImageResolution: LayoutImageResolution = {
    [ITEM_LAYOUT.LIST]: 250,
    [ITEM_LAYOUT.GRID]: 500,
    [ITEM_LAYOUT.GRID_MEDIUM]: 750,
    [ITEM_LAYOUT.GRID_LARGE]: 750
};

type ImageSizeArgs = {
    itemLayout?: ITEM_LAYOUT;
    isSiteWidthFull?: boolean;
    isMobile?: boolean;
};

export const getItemLayoutSrcSet = ({
    itemLayout = ITEM_LAYOUT.GRID,
    isSiteWidthFull = false,
    isMobile = false
}: ImageSizeArgs) => {
    const imageResolution = isSiteWidthFull
        ? fullWidthLayoutImageResolution[itemLayout]
        : layoutImageResolution[itemLayout];

    return isMobile
        ? pixelDensityResolution[imageResolution]
        : pixelDensityResolutionDesktop[imageResolution];
};

export const getSrc = (
    src: string,
    placeholder: string | RegExp,
    resolution?: ItemImageResolution
) => src.replace(placeholder, `${resolution || DEFAULT_RESOLUTION}-square`);

export const getSrcSet = (
    src: string,
    placeholder: string | RegExp,
    srcSetMap?: SrcSetMap
) => {
    if (!srcSetMap) {
        return getSrc(src, placeholder, DEFAULT_RESOLUTION);
    }

    return Object.entries(srcSetMap)
        .map(
            ([pixelDensity, resolution]) =>
                `${getSrc(src, placeholder, resolution)} ${pixelDensity}`
        )
        .join(', ');
};
